/**
 * VersionChecker - Detects when a new app version is deployed
 *
 * Polls version.json periodically to detect updates.
 * When a new version is detected, notifies the user to reload.
 */

interface VersionCheckerConfig {
    pollIntervalMs: number;
    versionUrl: string;
    onUpdateAvailable: (currentVersion: string, newVersion: string) => void;
}

const DEFAULT_CONFIG: VersionCheckerConfig = {
    pollIntervalMs: 5 * 60 * 1000, // 5 minutes
    versionUrl: '/version.json',
    onUpdateAvailable: () => {},
};

class VersionChecker {
    private config: VersionCheckerConfig;
    private currentVersion: string | null = null;
    private pollIntervalId: number | null = null;
    private isChecking = false;
    private updateAvailable = false;

    constructor(config: Partial<VersionCheckerConfig> = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
    }

    public async init(): Promise<void> {
        const version = await this.fetchVersion();
        if (version) {
            this.currentVersion = version;
            this.startPolling();
        }
    }

    public stop(): void {
        if (this.pollIntervalId !== null) {
            window.clearInterval(this.pollIntervalId);
            this.pollIntervalId = null;
        }
    }

    public isUpdateAvailable(): boolean {
        return this.updateAvailable;
    }

    public getCurrentVersion(): string | null {
        return this.currentVersion;
    }

    private async fetchVersion(): Promise<string | null> {
        try {
            const cacheBuster = Date.now();
            const response = await fetch(`${this.config.versionUrl}?t=${cacheBuster}`, {
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                },
            });

            if (!response.ok) {
                console.warn('[VersionChecker] Failed to fetch version:', response.status);
                return null;
            }

            const data = await response.json();
            return data.version || null;
        } catch (error) {
            console.warn('[VersionChecker] Error fetching version:', error);
            return null;
        }
    }

    private async checkForUpdate(): Promise<void> {
        if (this.isChecking || this.updateAvailable) {
            return;
        }

        this.isChecking = true;

        try {
            const newVersion = await this.fetchVersion();

            if (newVersion && this.currentVersion && newVersion !== this.currentVersion) {
                this.updateAvailable = true;
                this.config.onUpdateAvailable(this.currentVersion, newVersion);
            }
        } finally {
            this.isChecking = false;
        }
    }

    private startPolling(): void {
        if (this.pollIntervalId !== null) {
            return;
        }

        this.pollIntervalId = window.setInterval(() => {
            this.checkForUpdate();
        }, this.config.pollIntervalMs);
    }
}

// Singleton instance
let versionCheckerInstance: VersionChecker | null = null;

export function initVersionChecker(
    onUpdateAvailable: (currentVersion: string, newVersion: string) => void,
    pollIntervalMs: number = 5 * 60 * 1000,
): void {
    if (versionCheckerInstance) {
        return;
    }

    versionCheckerInstance = new VersionChecker({
        onUpdateAvailable,
        pollIntervalMs,
    });

    versionCheckerInstance.init();
}

export function stopVersionChecker(): void {
    if (versionCheckerInstance) {
        versionCheckerInstance.stop();
        versionCheckerInstance = null;
    }
}

export function isUpdateAvailable(): boolean {
    return versionCheckerInstance?.isUpdateAvailable() ?? false;
}

export default VersionChecker;
